import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Spacer } from "./Spacer";
import { TextCapitalized } from "./text/TextCapitalized";
import { ButtonDS } from "./Button";
import { PictureViewerDS } from "./PictureViewer";
import { IconCircle } from "./IconCircle";
import { FileInputButton } from "../../components/input/FileInputButton";
import { triggerToastDS } from "./TriggerToast";
import { colors } from "../../styles/figmaColors";
import { typographies } from "../../styles/figmaTypographies";
import { AddOutlined, RemoveRedEyeOutlined } from "../materialUi/materialUi";

const photoExtensionRegex = /\.(jpe?g|png)$/i;

export interface PhotosInputDSProps {
  newPictures: {
    photos: File[];
    setPhotos?: (files: File[]) => void;
  };
  oldPictures?: {
    photos: { uuid: string; url: string }[];
    onRemove?: (uuid: string) => void;
  };
  canAdd?: boolean;
  canRemove?: boolean;
  withDeleteIcon?: boolean;
  max?: number;
  withViewIcon?: boolean;
}

export const PhotosInputDS = ({
  max = 6,
  withViewIcon,
  ...props
}: PhotosInputDSProps) => {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>([]);
  const { withDeleteIcon } = props;
  const { photos, setPhotos } = props.newPictures;
  const oldPhotos = props.oldPictures?.photos ?? [];
  const onRemoveOld = props.canRemove ? props.oldPictures?.onRemove : undefined;

  useEffect(() => {
    const created = photos.map((file) => URL.createObjectURL(file));
    setUrls(created);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photos]);

  const add = (file: File | null) => {
    if (!file || !setPhotos) return;
    if (!photoExtensionRegex.test(file.name)) {
      triggerToastDS(t("photosInput.error.type") || "", "ERROR");
      return;
    }
    setPhotos([...photos, file]);
  };

  const removeNew =
    props.canRemove && setPhotos
      ? (index: number) => setPhotos(photos.filter((_, i) => i !== index))
      : undefined;

  const total = oldPhotos.length + photos.length;
  const showAdd = !!props.canAdd && !!setPhotos && total < max;

  const viewIcon = withViewIcon ? (
    <IconCircle
      icon={<RemoveRedEyeOutlined />}
      iconColor="textBlack"
      backgroundColor="textWhite"
      size="S"
    />
  ) : undefined;

  return (
    <StyledPhotos>
      {oldPhotos.map((picture) => (
        <PictureViewerDS
          key={picture.uuid}
          url={picture.url}
          icon={viewIcon}
          onDelete={
            withDeleteIcon && onRemoveOld
              ? () => onRemoveOld(picture.uuid)
              : undefined
          }
          bottomContent={
            !withDeleteIcon && onRemoveOld ? (
              <ButtonDS
                format="fill"
                buttonType="danger"
                text={t("photosInput.delete")}
                onClick={() => onRemoveOld(picture.uuid)}
              />
            ) : undefined
          }
        />
      ))}
      {urls.map((url, index) => (
        <PictureViewerDS
          key={url}
          url={url}
          icon={viewIcon}
          onDelete={
            withDeleteIcon && removeNew ? () => removeNew(index) : undefined
          }
          bottomContent={
            !withDeleteIcon && removeNew ? (
              <ButtonDS
                format="fill"
                buttonType="danger"
                text={t("photosInput.delete")}
                onClick={() => removeNew(index)}
              />
            ) : undefined
          }
        />
      ))}
      {showAdd && (
        <FileInputButton handleFile={add} accept="image/jpeg,image/png">
          <StyledAddTile>
            <StyledAddIcon>
              <AddOutlined />
            </StyledAddIcon>
            <Spacer y={0.5} />
            <TextCapitalized>{t("photosInput.add")}</TextCapitalized>
          </StyledAddTile>
        </FileInputButton>
      )}
    </StyledPhotos>
  );
};

const StyledPhotos = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const StyledAddTile = styled.div`
  ${typographies["Body/XS"]}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  box-sizing: border-box;
  text-align: center;
  color: ${colors["colors/text/lightGrey"]};
  background-color: ${colors["colors/text/white"]};
  border: 0.0625rem dashed ${colors["colors/borders/cards/cards"]};
  border-radius: 0.5rem;
`;

const StyledAddIcon = styled.div`
  display: flex;
  border-radius: 50%;
  border: 0.0625rem solid ${colors["colors/text/black"]};
  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${colors["colors/text/black"]};
  }
`;
